import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MediaDirectory, MediaFileEntry } from 'media';
import { Observable } from 'rxjs';

/** Error codes the admin API answers with (`{ error: <code> }`). */
export type SalesToolkitErrorCode =
  | 'exists'
  | 'name_invalid'
  | 'type_not_allowed'
  | 'too_large'
  | 'not_found'
  | 'forbidden'
  | 'path_invalid';

/** Extensions the backend accepts (SalesToolkitStore) - mirrored to filter before uploading. */
export const SALES_TOOLKIT_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'mp4'] as const;

const MIB = 1024 * 1024;

/** Per-file limit for images and PDFs (SalesToolkitStore). */
export const SALES_TOOLKIT_DEFAULT_MAX_BYTES = 20 * MIB;

/**
 * Per-file limit by extension, mirrored from SalesToolkitStore. Videos get
 * their own, much larger limit: a sales clip of a few minutes is already far
 * beyond what is sensible for a picture or a document, and raising one limit
 * for everything would let a 400 MB "image" through.
 */
const SALES_TOOLKIT_MAX_BYTES_BY_EXTENSION: Readonly<Record<string, number>> = {
  mp4: 500 * MIB
};

/** The backend's size limit for a file with this extension (lower case, no dot). */
export function maxBytesFor(extension: string): number {
  return SALES_TOOLKIT_MAX_BYTES_BY_EXTENSION[extension.toLowerCase()] ?? SALES_TOOLKIT_DEFAULT_MAX_BYTES;
}

/** The same limit in whole MB, as the upload queue names it ("larger than 500 MB"). */
export function maxMegabytesFor(extension: string): number {
  return Math.round(maxBytesFor(extension) / MIB);
}

/**
 * Admin file manager over the brand's Sales Toolkit folder.
 *
 * Never passes a brand: the backend binds every call to the config domain of the
 * host the request arrives on, so a distribution admin cannot reach Global
 * Health's folder even with the same account. Paths are relative to the toolkit
 * root and use the listing shape of `GET /media/browse`. Relative URLs - the
 * interceptors add the server URL and the bearer token.
 */
@Injectable({ providedIn: 'root' })
export class AdminSalesToolkitService {
  private readonly base = '/admin/sales_toolkit';

  constructor(private http: HttpClient) {}

  browse(path: string): Observable<MediaDirectory> {
    const params = new HttpParams().set('path', path || '');
    return this.http.get<MediaDirectory>(`${this.base}/browse`, { params });
  }

  createDirectory(path: string, name: string): Observable<MediaFileEntry> {
    return this.http.post<MediaFileEntry>(`${this.base}/directories`, { path, name });
  }

  /**
   * Uploads one file into `path`. `relativePath` (from a folder drop or a folder
   * picker, e.g. `Kampagne/Q4/bild.jpg`) places it below `path` and makes the
   * backend create the folders in between. Emits HTTP events for the progress bar.
   */
  uploadFile(path: string, file: File, options: { relativePath?: string; overwrite: boolean }): Observable<HttpEvent<unknown>> {
    const body = new FormData();
    body.append('path', path || '');
    body.append('file', file, file.name);
    if (options.relativePath) {
      body.append('relative_path', options.relativePath);
    }
    body.append('overwrite', options.overwrite ? 'true' : 'false');
    return this.http.post(`${this.base}/files`, body, { reportProgress: true, observe: 'events' });
  }

  rename(path: string, name: string): Observable<MediaFileEntry> {
    return this.http.patch<MediaFileEntry>(`${this.base}/entries`, { path, name });
  }

  /** Directories are removed with everything in them. */
  delete(path: string): Observable<void> {
    const params = new HttpParams().set('path', path);
    return this.http.delete<void>(`${this.base}/entries`, { params });
  }
}
